/* DesignSystem.tsx — Srinath's design system as a living styleguide page.
   Content mirrors the living principles doc (CLAUDE.md): confirmed principles
   first, then the derived house style (color logic, type, road anatomy).
   Route: /design-system. */

import { data } from "../lib/data";
import { ShareThoughts } from "../components/ShareThoughts";

interface DSPrinciple {
  n: string;
  t: string;
  d: string;
}

const DS_PRINCIPLES: DSPrinciple[] = [
  {
    n: "01",
    t: "Geometry carries meaning",
    d: "Spacing and position are arguments, not decoration. Every coordinate should be defensible from the data — if we can't explain why something sits where it sits, it isn't finished.",
  },
  {
    n: "02",
    t: "Constraints first",
    d: "Place the hard constraints first (the Employment cards), then let everything else — decade markers, bends, the road itself — resolve around them. Never the reverse.",
  },
  {
    n: "03",
    t: "Two registers of one truth",
    d: "Cards show granular progress; decade markers abstract the same career into broad strokes. Both read the same timeline at different zoom levels.",
  },
  {
    n: "04",
    t: "Reserve space for the future",
    d: "The '20s marker sits where a hypothetical 4th hairpin would end. Structure anticipates data that doesn't exist yet.",
  },
  {
    n: "05",
    t: "Asymmetry is information",
    d: "The shapes carved by the hairpin bends must each be distinctly different. When cards line up or shapes mirror, the design is lying about the data — real careers aren't symmetric.",
  },
  {
    n: "06",
    t: "Earned illusion",
    d: "The first and latest cards deliberately don't align — the offset reads as elevation, like walking up a winding, inclined trail. Small intentional misalignments encode felt qualities.",
  },
  {
    n: "07",
    t: "Learn, don't mimic",
    d: "External sources are inputs to reason about, never templates. Gaps get filled by inference from the data, then verified — not by borrowed convention.",
  },
];

/* the Eras card sequence — warm/cool ordering so warms never touch */
const DS_ROAD_SEQ: { c: string; n: string }[] = [
  { c: "#e5484d", n: "Red" },
  { c: "#41a85f", n: "Green" },
  { c: "#f2820c", n: "Orange" },
  { c: "#2f6fe0", n: "Blue" },
  { c: "#d9a400", n: "Gold" },
  { c: "#8e4ec6", n: "Violet" },
];

const DS_LABEL_PALETTE: string[] = [
  "#cf5a4e",
  "#d08a3a",
  "#4fa463",
  "#3f9aa6",
  "#5b85e0",
  "#9a72e0",
  "#c25b97",
  "#8a93a3",
];

type SwatchItem = string | { c: string; n: string };

function DSSwatchRow({ items }: { items: SwatchItem[] }) {
  return (
    <div className="ds-swatches">
      {items.map((s, i) => {
        const color = typeof s === "string" ? s : s.c;
        const label = typeof s === "string" ? s : s.n;
        return (
          <div className="ds-swatch" key={i}>
            <span className="ds-swatch-chip" style={{ background: color }} />
            <span className="mono ds-swatch-label">{label}</span>
          </div>
        );
      })}
    </div>
  );
}

/* Type-specimen meta line, derived from the data at render time (never hardcoded). */
function specimenMetaLine(): string {
  const currentYear = new Date().getFullYear();
  const startYears = data.experiences.map((e) => Number(e.start.slice(0, 4)));
  const endYears = data.experiences.map((e) =>
    e.end === "present" ? currentYear : Number(e.end.slice(0, 4)),
  );
  const minYear = startYears.length ? Math.min(...startYears) : currentYear;
  const maxYear = endYears.length ? Math.max(...endYears) : currentYear;
  return `${minYear} – ${maxYear} · ${data.experiences.length} ROLES · ${data.industries.length} INDUSTRIES`;
}

export function DesignSystem() {
  return (
    <div className="wrap section ds-page">
      <p className="eyebrow">Design system</p>
      <h1 className="display" style={{ fontSize: 44, marginTop: 10 }}>
        How this résumé thinks
      </h1>
      <p className="section-sub" style={{ maxWidth: "62ch" }}>
        Not a component library — a reasoning system. The visual aesthetic is downstream of
        logical inferences about the underlying data and the relationships within it.
        This page is generated from the living principles doc and updates as rulings land.
      </p>

      <h2 className="display ds-h2">Principles</h2>
      <ol className="ds-principles">
        {DS_PRINCIPLES.map((p) => (
          <li key={p.n} className="card ds-principle">
            <span className="mono ds-principle-n">{p.n}</span>
            <div>
              <h3 className="ds-principle-t">{p.t}</h3>
              <p className="ds-principle-d">{p.d}</p>
            </div>
          </li>
        ))}
      </ol>

      <h2 className="display ds-h2">Color logic</h2>
      <div className="ds-grid">
        <div className="card card-pad">
          <p className="ds-card-kicker mono">Eras card sequence</p>
          <p className="ds-card-sub">
            Bright primaries repeated in fixed order — no data meaning, just a micro-context
            beat per card. Rule: the three warms (red · orange · gold) never sit adjacent, and
            blue never touches violet. The warm/cool alternation below guarantees it.
          </p>
          <DSSwatchRow items={DS_ROAD_SEQ} />
        </div>
        <div className="card card-pad">
          <p className="ds-card-kicker mono">Label palette (theme-safe)</p>
          <p className="ds-card-sub">
            The only hues offered for era highlights and decade markers — mid-tone, readable
            on both light and dark. Curated set, never a free picker.
          </p>
          <DSSwatchRow items={DS_LABEL_PALETTE} />
        </div>
        <div className="card card-pad">
          <p className="ds-card-kicker mono">Reasoned, not picked</p>
          <p className="ds-card-sub">
            Per-entity hues share oklch lightness and chroma and vary only hue — adjacent
            items can't clash and the palette survives theme flips. Roles color by seniority;
            skills sit on an evenly-spaced hue wheel; the road is asphalt: greyscale,
            mid-tone, "well-worn".
          </p>
          <div className="ds-roadstrip" aria-hidden="true">
            <span />
          </div>
        </div>
      </div>

      <h2 className="display ds-h2">Type</h2>
      <div className="ds-grid">
        <div className="card card-pad">
          <p className="ds-card-kicker mono">Display — Newsreader</p>
          <p className="ds-type-display">Three decades, one winding road</p>
          <p className="ds-card-sub">
            Editorial serif for titles. Italic = the author's own voice (era notes, decade
            markers).
          </p>
        </div>
        <div className="card card-pad">
          <p className="ds-card-kicker mono">Body — Hanken Grotesk</p>
          <p className="ds-type-body">
            Cards show granular progress; decade markers show broad strokes. Both read the
            same timeline at different zoom levels.
          </p>
          <p className="ds-card-sub">
            Workhorse for everything readable. Metric values: bold, dark grey — never loud.
          </p>
        </div>
        <div className="card card-pad">
          <p className="ds-card-kicker mono">Meta — JetBrains Mono</p>
          <p className="ds-type-mono">{specimenMetaLine()}</p>
          <p className="ds-card-sub">
            Years, counts, hints, and editorial controls. Uppercase, tracked out, quiet.
          </p>
        </div>
      </div>

      <h2 className="display ds-h2">Working agreements</h2>
      <ul className="ds-rules">
        <li>
          Placed geometry (cards, bends, markers) is authored — propose new coordinates and
          show them before baking; never silently re-flow.
        </li>
        <li>
          New data relationships get a proposed visual encoding first, a ruling second,
          pixels third.
        </li>
        <li>
          Authoring tools live in the artifact (drag, bend, recolor, resize, copy-layout)
          and persist locally until baked.
        </li>
        <li>
          Restraint: hairline rules, soft shadows, translucency. No gradient slop, no emoji.
        </li>
      </ul>

      {data.glossary && data.glossary.length > 0 && (
        <section className="ds-section">
          <h2 className="display ds-h2">Glossary</h2>
          <p className="ds-glossary-intro">
            Acronyms as used across this résumé — so AI assistants (and readers) resolve them to the
            right meaning, not a similarly-spelled one. The same list grounds the{" "}
            <a href="/corpus/resume.md" className="hero-meta-link" target="_blank" rel="noopener">
              AI-readable corpus
            </a>
            .
          </p>
          <dl className="ds-glossary">
            {data.glossary.map((g) => (
              <div className="ds-glossary-row" key={g.term}>
                <dt className="ds-glossary-term">
                  <span className="mono">{g.term}</span> {g.expansion}
                </dt>
                {g.note && <dd className="ds-glossary-note">{g.note}</dd>}
              </div>
            ))}
          </dl>
        </section>
      )}

      <ShareThoughts className="page-share-bottom" />
    </div>
  );
}
